/**
 * Zeichnungsberechtigte — the CUSTOMER firm's register signatories card on the Kunde Übersicht.
 *
 * Stefan 2026-10-06 ("Eine Zeichnungsberechtigte haben wir noch nicht"): show WHO may sign for
 * the firm, per the Handelsregister. Distinct from the Ansprechpersonen card above (who we TALK
 * to) and from the broker-side admin "Zeichnungsberechtigte Personen" (broker_signatory).
 *
 * Read-only. The one write is the Handelsregisterauszug upload (POST /firms/:id/excerpt), which
 * the existing doc-extraction worker turns into the winning excerpt set. There is deliberately
 * NO enrich button here — that lives in the Firmenstammdaten card one level up and rewrites the
 * SHAB replay set. `refreshKey` lets the screen force a refetch of this card; the screen bumps it
 * after a Firmenstammdaten EDIT (onSaved), after the edit dialog, AND after a successful manual
 * re-enrichment (FirmenStammdaten's onEnriched → the screen's setFirmRefreshKey), so a
 * re-enrichment that rewrites the replay set is reflected here immediately.
 *
 * Data: the api picks the effective set (excerpt wins, else replay) and sorts it; this card
 * renders the list as delivered and never re-sorts. The five empty states are honest: they say
 * exactly why there is nothing to show and what the broker can do about it.
 *
 * [Source: apps/web/src/features/entity-detail/components/Hauptansprechperson.tsx] (card + async-state precedent)
 * [Source: apps/web/src/features/entity-detail/components/FirmenStammdaten.tsx] (enrich poll idiom)
 */

import { useEffect, useRef, useState } from 'react';
import { AlertCircle, FileText, Loader2, ShieldCheck, Upload } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { apiClient, ApiError } from '../../../lib/api-client';
import type { FirmSignatoriesWire } from '../../../lib/api-client';
import { Badge } from '../../../components/ui/Badge';
import { dateShortDe } from '../../../lib/format';
import { isLiveData } from '../../../lib/data-mode';
import { signatureLabel, signatureTone } from './signature-label';

export interface ZeichnungsberechtigteProps {
  firmaId: string;
  /** Holds `crm.write`: may upload a Handelsregisterauszug. */
  canUpload: boolean;
  /** Bumped by the screen after a Firmenstammdaten EDIT (onSaved / edit dialog) to force a
   *  refetch. The manual-enrich button does not bump it yet — see the header note. */
  refreshKey?: number;
}

/** How long the upload polls firmSignatories.get() for the new excerpt set before giving up. */
const EXCERPT_POLL_INTERVAL_MS = 1500;
const EXCERPT_POLL_TIMEOUT_MS = 15000;

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

type UploadMessage = { kind: 'info' | 'error'; text: string } | null;

export function Zeichnungsberechtigte({ firmaId, canUpload, refreshKey }: ZeichnungsberechtigteProps) {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Survives across the async upload poll so a firm change / unmount cancels it.
  const uploadCancelledRef = useRef(false);

  const [data, setData] = useState<FirmSignatoriesWire | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [restricted, setRestricted] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState<UploadMessage>(null);

  useEffect(() => {
    // Mock mode has no backend to read; the card renders null below. Skip the fetch entirely so
    // it never hits a non-existent /api in the mock UI (matches FirmenDomains' live-only stance).
    if (!isLiveData()) return;
    let cancelled = false;
    // NOTE: the upload-cancel ref is NOT reset here. Resetting it on every load (incl. a
    // refreshKey bump) would "un-cancel" an in-flight upload that this same re-run just
    // cancelled via the cleanup below. A new upload clears the ref itself in onFileChange.
    setLoading(true);
    setRestricted(false);
    apiClient.firmSignatories
      .get(firmaId)
      .then((res) => {
        if (!cancelled) {
          setData(res);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 403) {
          setRestricted(true);
          setError(null);
        } else {
          setError(
            err instanceof Error
              ? err.message
              : 'Die Zeichnungsberechtigten konnten nicht geladen werden.',
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      uploadCancelledRef.current = true;
    };
  }, [firmaId, refreshKey]);

  // Live-only: in mock mode there is no backend to read (same as FirmenDomains). Placed after
  // every hook so the rules of hooks hold.
  if (!isLiveData()) return null;

  const onPickFile = () => fileInputRef.current?.click();

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset the input so re-selecting the same file fires change again.
    e.target.value = '';
    if (!file) return;

    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    if (!isPdf) {
      setUploadMsg({ kind: 'error', text: 'Bitte eine PDF-Datei auswählen.' });
      return;
    }

    // A new upload owns the cancel ref from here on (the load effect no longer resets it).
    uploadCancelledRef.current = false;
    setUploading(true);
    setUploadMsg({ kind: 'info', text: 'Auszug wird hochgeladen …' });

    // Phase 1 — the upload itself. A failure here means the extract did NOT reach the server.
    let documentId: string;
    try {
      const res = await apiClient.firmSignatories.uploadExcerpt(firmaId, file);
      documentId = res.document_id;
    } catch (err: unknown) {
      if (uploadCancelledRef.current) return;
      setUploadMsg({
        kind: 'error',
        text: err instanceof Error ? err.message : 'Der Auszug konnte nicht hochgeladen werden.',
      });
      setUploading(false);
      return;
    }
    if (uploadCancelledRef.current) return;
    setUploadMsg({ kind: 'info', text: 'Auszug hochgeladen, wird verarbeitet …' });

    // Phase 2 — poll for the parsed set. The upload already SUCCEEDED (202), so a failing poll
    // read must NOT read as an upload error; it only means the result is not visible yet.
    try {
      const deadline = Date.now() + EXCERPT_POLL_TIMEOUT_MS;
      for (;;) {
        if (uploadCancelledRef.current) return;
        const latest = await apiClient.firmSignatories.get(firmaId);
        if (uploadCancelledRef.current) return;
        const ready = (latest.sets ?? []).some(
          (s) => s.source === 'excerpt_pdf' && s.document_id === documentId,
        );
        if (ready) {
          setData(latest);
          setUploadMsg(null);
          return;
        }
        if (Date.now() >= deadline) {
          setData(latest);
          setUploadMsg({
            kind: 'info',
            text: 'Die Verarbeitung dauert länger. Die Liste aktualisiert sich beim nächsten Laden.',
          });
          return;
        }
        await sleep(EXCERPT_POLL_INTERVAL_MS);
      }
    } catch {
      // The extract is uploaded and queued; only the status read failed. Say exactly that
      // (not "Upload fehlgeschlagen"), and let the next load surface the result.
      if (uploadCancelledRef.current) return;
      setUploadMsg({
        kind: 'info',
        text: 'Auszug hochgeladen. Der Status konnte nicht geladen werden; die Liste aktualisiert sich beim nächsten Laden.',
      });
    } finally {
      if (!uploadCancelledRef.current) setUploading(false);
    }
  };

  const openDocument = (documentId: string) => {
    void navigate({ to: '/ablage/$id', params: { id: documentId } });
  };

  // ── Derived view data ───────────────────────────────────────────────────────
  const sets = data?.sets ?? [];
  const sigs = data?.signatories ?? [];
  const effectiveSource = data?.effective_source ?? null;
  const hasReplaySet = sets.some((s) => s.source === 'zefix_replay');
  const zefixStatus = data?.zefix_status;
  const uid = data?.uid ?? null;
  const incompleteHistory = data?.incomplete_history === true;
  const discrepancy = data?.discrepancy ?? null;

  // Exactly one body state. MAJOR fix (AC12 = never INVENT a list, not hide a REAL one): the
  // no-UID / checking / failed states only replace the body when there is NO stored set. A firm
  // that already has a replay/excerpt set keeps showing it during a nightly re-enrichment
  // (pending/in_progress), after a failed retry, or after a broker clears the UID — the list is
  // real, so it stays; the transient status is shown as a hint ABOVE the rows instead.
  const emptyState: 'no-uid' | 'checking' | 'failed' | 'no-set' | 'empty-set' | null =
    data === null
      ? null
      : sets.length === 0
        ? uid === null
          ? 'no-uid'
          : zefixStatus === 'pending' || zefixStatus === 'in_progress'
            ? 'checking'
            : zefixStatus === 'failed'
              ? 'failed'
              : 'no-set'
        : sigs.length === 0
          ? 'empty-set'
          : null;

  // Shared hints (incomplete SHAB history + excerpt-vs-SHAB discrepancy). Rendered both under a
  // real list AND under the "empty-set" body, so an empty excerpt that disagrees with a non-empty
  // replay still surfaces the discrepancy (MINOR #6).
  const hints = (
    <>
      {incompleteHistory && effectiveSource === 'zefix_replay' && (
        <p className="mt-2 flex items-start gap-2 text-xs text-muted-foreground">
          <AlertCircle className="size-3.5 shrink-0" />
          Die SHAB-Historie ist möglicherweise unvollständig. Ein hochgeladener
          Handelsregisterauszug ersetzt diese Liste.
        </p>
      )}
      {discrepancy?.differs === true && (
        <details className="mt-2 text-xs">
          <summary className="flex cursor-pointer items-start gap-2 text-status-warning">
            <AlertCircle className="size-3.5 shrink-0" />
            Auszug und SHAB weichen ab: {(discrepancy.only_in_excerpt ?? []).length} nur im Auszug,{' '}
            {(discrepancy.only_in_replay ?? []).length} nur im SHAB. Eine Prüfaufgabe wurde erstellt.
          </summary>
          <div className="mt-1.5 flex flex-col gap-1 pl-5 text-muted-foreground">
            {(discrepancy.only_in_excerpt ?? []).map((s, i) => (
              <span key={`exc-${i}`}>Nur im Auszug: {s.display_name}</span>
            ))}
            {(discrepancy.only_in_replay ?? []).map((s, i) => (
              <span key={`rep-${i}`}>Nur im SHAB: {s.display_name}</span>
            ))}
          </div>
        </details>
      )}
    </>
  );

  return (
    <section className="rounded-lg border bg-card" aria-label="Zeichnungsberechtigte">
      <header className="border-b px-4 py-3">
        <h3 className="text-sm font-semibold">Zeichnungsberechtigte</h3>
        <p className="text-xs text-muted-foreground">
          Wer für die Firma unterschreiben darf, gemäss Handelsregister. Nicht zu verwechseln mit
          den Ansprechpersonen.
        </p>
      </header>

      <div className="px-4 py-3">
        {loading && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            Wird geladen …
          </p>
        )}

        {!loading && restricted && (
          <p className="flex items-start gap-2 text-sm text-muted-foreground">
            <ShieldCheck className="size-4 shrink-0" />
            Eingeschränkt: Für diese Firma dürfen Sie die Zeichnungsberechtigten nicht sehen.
          </p>
        )}

        {!loading && !restricted && error !== null && (
          <p className="flex items-start gap-2 text-sm text-destructive">
            <AlertCircle className="size-4 shrink-0" />
            Die Zeichnungsberechtigten konnten nicht geladen werden.
          </p>
        )}

        {!loading && !restricted && error === null && data !== null && (
          <>
            {emptyState === 'no-uid' && (
              <p className="text-sm text-muted-foreground">
                Keine UID hinterlegt. Ohne UID kann das Handelsregister nicht abgefragt werden. Die
                UID lässt sich in der Firmenstammdaten-Kachel erfassen.
              </p>
            )}

            {emptyState === 'checking' && (
              <p className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="size-4 animate-spin" />
                Wird mit dem Handelsregister abgeglichen …
              </p>
            )}

            {emptyState === 'failed' && (
              <p className="text-sm text-muted-foreground">
                Der Handelsregister-Abgleich ist fehlgeschlagen. Erneut versuchen über die
                Firmenstammdaten-Kachel.
              </p>
            )}

            {emptyState === 'no-set' && (
              <p className="text-sm text-muted-foreground">
                Noch keine Zeichnungsberechtigten ermittelt. Über die Firmenstammdaten-Kachel
                aktualisieren oder einen Handelsregisterauszug hochladen.
              </p>
            )}

            {emptyState === 'empty-set' && (
              <>
                <p className="text-sm text-muted-foreground">
                  Im Handelsregister sind keine Zeichnungsberechtigten publiziert.
                </p>
                {hints}
              </>
            )}

            {emptyState === null && (
              <>
                {/* Transient-status hints ABOVE the real list (MAJOR fix): the stored signatories
                    stay visible during a re-enrichment or after the UID was cleared; the status is
                    a hint, never a replacement for a real list. */}
                {(zefixStatus === 'pending' || zefixStatus === 'in_progress') && (
                  <p className="mb-2 flex items-center gap-2 text-xs text-muted-foreground">
                    <Loader2 className="size-3.5 animate-spin" />
                    Wird gerade mit dem Handelsregister abgeglichen. Unten der letzte Stand.
                  </p>
                )}
                {uid === null && (
                  <p className="mb-2 flex items-start gap-2 text-xs text-muted-foreground">
                    <AlertCircle className="size-3.5 shrink-0" />
                    Keine UID hinterlegt. Die Liste zeigt den zuletzt ermittelten Stand.
                  </p>
                )}

                {/* Source line */}
                {effectiveSource === 'excerpt_pdf' && (
                  <p className="mb-2 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                    {data.document_id != null ? (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 rounded-md border border-status-info-border bg-status-info-bg px-2 py-0.5 text-xs font-medium text-status-info hover:underline"
                        onClick={() => openDocument(data.document_id as string)}
                      >
                        <FileText className="size-3" aria-hidden />
                        Handelsregisterauszug
                      </button>
                    ) : (
                      <Badge variant="info">Handelsregisterauszug</Badge>
                    )}
                    <span>vom {dateShortDe(data.fetched_at)}</span>
                    {hasReplaySet && <span>· SHAB-Abgleich vorhanden</span>}
                  </p>
                )}
                {effectiveSource === 'zefix_replay' && (
                  <p className="mb-2 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                    <Badge variant="neutral">Handelsregister (SHAB)</Badge>
                    <span>Stand {dateShortDe(data.fetched_at)}</span>
                  </p>
                )}

                {/* Rows */}
                <ul className="flex flex-col gap-1">
                  {sigs.map((s, i) => {
                    const code = s.signature ?? '';
                    const addedIn = s.added_in ?? null;
                    const showAddedIn = addedIn !== null && /^\d{4}/.test(addedIn);
                    return (
                      <li
                        key={`${s.last_name ?? ''}-${s.first_name ?? ''}-${i}`}
                        data-testid="zb-row"
                        className="flex items-center gap-2 rounded px-2 py-1.5 text-sm"
                      >
                        <span className="min-w-0 flex-1 truncate">
                          <span className="font-medium">{s.display_name}</span>
                          {s.role != null && (
                            <span className="text-muted-foreground"> · {s.role}</span>
                          )}
                          {showAddedIn && (
                            <span className="block truncate text-xs text-muted-foreground">
                              seit {addedIn}
                            </span>
                          )}
                        </span>
                        <Badge variant={signatureTone(code)} className="shrink-0">
                          {signatureLabel(code)}
                        </Badge>
                      </li>
                    );
                  })}
                </ul>

                {/* Hints (incomplete SHAB history + discrepancy) — shared with the empty-set body. */}
                {hints}
              </>
            )}

            {/* Upload action (crm.write only). MINOR #2: without a UID the doc-extraction worker
                skips cantonal_excerpt, so the upload would silently never produce a set and the
                poll would time out with a misleading "dauert länger". Disable it and say why. */}
            {canUpload && (
              <div className="mt-3 border-t pt-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  disabled={uid === null}
                  onChange={(e) => void onFileChange(e)}
                  data-testid="zb-excerpt-input"
                />
                <button
                  type="button"
                  disabled={uploading || uid === null}
                  onClick={onPickFile}
                  className="flex items-center gap-1.5 rounded px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-muted disabled:opacity-50"
                >
                  <Upload className="size-3.5" aria-hidden />
                  Handelsregisterauszug hochladen
                </button>
                {uid === null ? (
                  <p className="mt-2 text-xs text-muted-foreground">
                    Ohne hinterlegte UID kann kein Handelsregisterauszug verarbeitet werden. UID
                    zuerst in der Firmenstammdaten-Kachel erfassen.
                  </p>
                ) : (
                  uploadMsg !== null && (
                    <p
                      className={`mt-2 text-xs ${
                        uploadMsg.kind === 'error' ? 'text-destructive' : 'text-muted-foreground'
                      }`}
                    >
                      {uploadMsg.text}
                    </p>
                  )
                )}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
